<template>
  <div class="interview-time-range" :class="hasError && 'is-error'">
    <span class="time-icon"><i class="el-icon-time"></i></span>
    <div class="date-select">
      <el-date-picker
        v-model="startDate"
        type="date"
        value-format="yyyy-MM-dd"
        class="start-date-piker"
        :class="(startDateError || errorMessage) && 'is-error'"
        :placeholder="$t(`${i18nPrefix}.placeholder.selectDate`)"
        :picker-options="datePickerOptions"
        @change="handleDateChange"
      >
      </el-date-picker>
      <div v-if="startDateError || errorMessage" class="error">{{ startDateError || errorMessage }}</div>
    </div>
    <div class="start-time">
      <TimePicker
        v-model="startTime"
        useScrollSearch
        class="start-time-picker"
        :class="(startTimeError || errorMessage) && 'is-error'"
        :popperClass="`popover-start-time-${index}`"
        :placeholder="$t(`${i18nPrefix}.placeholder.selectStartTime`)"
        :minHour="startMinHour"
        :minMinute="startMinMinute"
        :minuteStep="30"
        @change="handleStartTimeChange"
      ></TimePicker>
      <div v-if="startTimeError" class="error">{{ startTimeError }}</div>
    </div>
    <span class="time-right"><i class="iconfont iconTo"></i></span>
    <div class="end-time" @click="handleEndTimeClick">
      <TimePicker
        v-model="endTime"
        disabled
        class="end-time-picker"
      ></TimePicker>
      <div class="overlay"></div>
    </div>
    <el-button class="delete-time-btn" type="text" @click="handleDeleteTime">
      <i class="icomoon-remove-box icon-btn"></i>
    </el-button>
  </div>
</template>

<script>
import TimePicker from '@/components/time/timePicker.vue'
import moment from 'moment'

export default {
  components: {
    TimePicker
  },
  props: {
    durationMinutes: {
      type: Number,
      default: 0
    },
    interviewDateTime: {
      type: Object,
      default: () => ({})
    },
    index: {
      type: Number,
      default: 0
    },
    errorMessage: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      startDate: null,
      startTime: null,
      startDateError: null,
      startTimeError: null,
      datePickerOptions: {
        disabledDate (time) {
          return time.getTime() < new Date().getTime() - 24 * 60 * 60 * 1000 // 24 hours
        }
      }
    }
  },
  computed: {
    startMinHour () {
      const isToDay = moment(this.startDate).isSame(moment(), 'day')
      return isToDay ? moment().hour() : 0
    },
    startMinMinute () {
      const isToDay = moment(this.startDate).isSame(moment(), 'day')
      return isToDay ? moment().minute() : 0
    },
    endTime () {
      return this.startTime ? moment(this.startTime, 'HH:mm').add(this.durationMinutes, 'minutes').format('HH:mm') : ''
    },
    hasError () {
      return this.startDateError || this.startTimeError || this.errorMessage
    }
  },
  created () {
    this.startDate = this.interviewDateTime.date
    this.startTime = this.interviewDateTime.time ? moment(this.interviewDateTime.time, 'HH:mm').format('HH:mm:ss') : null
  },
  methods: {
    handleDateChange () {
      this.startTime = null
      this.$emit('change', {
        date: this.startDate,
        time: null,
        dateTime: this.startDate
      })
    },
    handleStartTimeChange (value) {
      this.startTime = value
      this.$emit('change', {
        date: this.startDate,
        time: value,
        dateTime: moment(`${this.startDate} ${value}`, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm')
      })
    },
    submit () {
      let valid = true
      if (!this.startDate) {
        valid = false
        this.startDateError = this.$t('common.message.requiredFormField')
      }
      if (!this.startTime) {
        valid = false
        this.startTimeError = this.$t('common.message.requiredFormField')
      }
      return valid
    },
    handleDeleteTime () {
      this.$emit('deleteTime')
    },
    handleEndTimeClick () {
      this.$message.info(this.$t(`${this.i18nPrefix}.message.changeDuration`))
      this.$emit('endTimeClick')
    }
  }
}
</script>

<style scoped lang="scss">
.interview-time-range {
  display: flex;
  align-items: center;

  .time-icon {
    color: #0096cc;
    margin-right: 10px;
  }

  .date-select {
    margin-right: 8px;
    position: relative;
    flex: 3;

    .start-date-piker {
      width: 100%;

      &.is-error {
        ::v-deep {
          .el-input__inner {
            border-color: #f56c6c;
          }
        }
      }
    }
  }

  .start-time {
    position: relative;
    flex: 2;

    .start-time-picker {
      &.is-error {
        ::v-deep {
          .el-input__inner {
            border-color: #f56c6c;
          }
        }
      }
    }

  }

  .end-time {
    position: relative;
    flex: 2;

    .overlay {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      cursor: not-allowed;
    }
  }

  .time-right {
    padding: 0 4px;
  }

  .delete-time-btn {
    margin-left: 8px;
    padding: 0;

    .icon-btn {
      font-size: 20px;

      &::before {
        color: #0096cc;
      }
    }
  }

  .error {
    position: absolute;
    left: 0;
    padding-top: 4px;
    color: #f56c6c;
    font-size: 12px;
    line-height: 1;
  }

  &.is-error {
    margin-bottom: 18px;
  }

  ::v-deep {
    .el-input {
      .el-input__inner {
        height: 30px;
        line-height: 30px;
        border-radius: 2px;
        color: #000;

        &.is-disabled {
          background-color: #f2f2f2;
        }
      }

      .el-input__prefix {
        .el-input__icon {
          line-height: 30px;
          font-size: 18px;
        }
      }

      .el-input__suffix {
        .el-input__icon {
          line-height: 30px;
        }
      }
    }
  }
}
</style>
